/**
 * PE Fund Overview Page
 *
 * Displays a filterable fund list with expandable detail panels showing
 * performance metrics, LP interest positions, and capital activity history.
 * Data is fetched from the PE Valuation API.
 */
import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  Heading,
  BodyText,
  Button,
  Select,
  MetricDisplay,
  type SelectOption,
  TableScroll,
} from '@aaimplatform/ui';
import { createPeFundClient } from '@aaimplatform/institutional-types/api-clients';

const peFundClient = createPeFundClient();

// ---------------------------------------------------------------------------
// Option lists
// ---------------------------------------------------------------------------

const STRATEGY_FILTER_OPTIONS: SelectOption[] = [
  { value: '', label: 'All Strategies' },
  { value: 'large_cap_buyout', label: 'Large Cap Buyout' },
  { value: 'mid_market_buyout', label: 'Mid Market Buyout' },
  { value: 'small_cap_buyout', label: 'Small Cap Buyout' },
  { value: 'growth_equity', label: 'Growth Equity' },
  { value: 'venture_capital', label: 'Venture Capital' },
  { value: 'permanent_capital', label: 'Permanent Capital' },
  { value: 'co_investment', label: 'Co-Investment' },
  { value: 'secondary', label: 'Secondary' },
];

const SECTOR_FILTER_OPTIONS: SelectOption[] = [
  { value: '', label: 'All Sectors' },
  { value: 'technology', label: 'Technology' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'industrials', label: 'Industrials' },
  { value: 'consumer', label: 'Consumer' },
  { value: 'financial_services', label: 'Financial Services' },
  { value: 'energy', label: 'Energy' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'diversified', label: 'Diversified' },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

import type { components } from '@aaimplatform/institutional-types/pe-fund-analytics';

type PeFund = components['schemas']['PeFund'];
type FundPerformanceMetrics = components['schemas']['FundPerformanceMetrics'];
type LpInterest = components['schemas']['LpInterest'];
type CapitalActivity = components['schemas']['CapitalActivity'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatPercent(decimal: number): string {
  return `${(decimal * 100).toFixed(2)}%`;
}

function formatMultiple(value: number): string {
  return `${value.toFixed(2)}x`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function capitalizeLabel(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-growth/10 text-growth',
  fundraising: 'bg-accent/10 text-accent',
  investing: 'bg-warning/10 text-warning',
  harvesting: 'bg-warning/10 text-warning',
  closed: 'bg-recessed text-secondary',
  liquidating: 'bg-error/10 text-error',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PeFundOverviewPage() {
  const [strategyFilter, setStrategyFilter] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [expandedFundId, setExpandedFundId] = useState<string | null>(null);

  // Fetch fund list
  const {
    data: funds,
    isLoading: fundsLoading,
    error: fundsError,
  } = useQuery<PeFund[]>({
    queryKey: ['pe-funds', strategyFilter, sectorFilter],
    queryFn: async () => {
      type FundStrategy = components['schemas']['FundStrategy'];
      type SectorFocus = components['schemas']['SectorFocus'];
      const { data, error } = await peFundClient.GET('/api/v1/pe/funds', {
        params: {
          query: {
            ...(strategyFilter ? { strategy: strategyFilter as FundStrategy } : {}),
            ...(sectorFilter ? { sectorFocus: sectorFilter as SectorFocus } : {}),
          },
        },
      });
      if (error || !data) throw new Error('Failed to load funds');
      return data.data;
    },
  });

  // Fetch expanded fund details
  const {
    data: performance,
    isLoading: perfLoading,
  } = useQuery<FundPerformanceMetrics>({
    queryKey: ['pe-fund-performance', expandedFundId],
    queryFn: async () => {
      const { data, error } = await peFundClient.GET(
        '/api/v1/pe/funds/{fundId}/performance',
        { params: { path: { fundId: expandedFundId! } } }
      );
      if (error || !data) throw new Error('Failed to load performance');
      return data;
    },
    enabled: !!expandedFundId,
  });

  const {
    data: lpInterests,
    isLoading: lpLoading,
  } = useQuery<LpInterest[]>({
    queryKey: ['pe-fund-lp-interests', expandedFundId],
    queryFn: async () => {
      const { data, error } = await peFundClient.GET(
        '/api/v1/pe/funds/{fundId}/lp-interests',
        { params: { path: { fundId: expandedFundId! } } }
      );
      if (error || !data) throw new Error('Failed to load LP interests');
      return data.data;
    },
    enabled: !!expandedFundId,
  });

  const {
    data: capitalActivity,
    isLoading: activityLoading,
  } = useQuery<CapitalActivity[]>({
    queryKey: ['pe-fund-capital-activity', expandedFundId],
    queryFn: async () => {
      const { data, error } = await peFundClient.GET(
        '/api/v1/pe/funds/{fundId}/capital-activity',
        { params: { path: { fundId: expandedFundId! } } }
      );
      if (error || !data) throw new Error('Failed to load capital activity');
      return data.data;
    },
    enabled: !!expandedFundId,
  });

  const toggleExpand = useCallback((fundId: string) => {
    setExpandedFundId((prev) => (prev === fundId ? null : fundId));
  }, []);

  const detailLoading = perfLoading || lpLoading || activityLoading;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <BodyText className="text-secondary mt-2">
            Browse private equity funds with performance, LP positions, and capital activity
          </BodyText>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Select
              label="Strategy"
              options={STRATEGY_FILTER_OPTIONS}
              value={strategyFilter}
              onChange={(e) => {
                setStrategyFilter(e.target.value);
                setExpandedFundId(null);
              }}
            />
          </div>
          <div>
            <Select
              label="Sector"
              options={SECTOR_FILTER_OPTIONS}
              value={sectorFilter}
              onChange={(e) => {
                setSectorFilter(e.target.value);
                setExpandedFundId(null);
              }}
            />
          </div>
        </div>
      </Card>

      {/* Fund List */}
      {fundsLoading && (
        <Card>
          <BodyText className="text-secondary text-center py-8">Loading funds...</BodyText>
        </Card>
      )}

      {fundsError && (
        <Card>
          <div className="p-4 bg-error/10 border border-error/20 rounded">
            <BodyText className="text-error font-semibold">Error Loading Funds</BodyText>
            <BodyText className="text-error mt-1">
              Failed to retrieve fund list. Verify the PE Valuation service is available.
            </BodyText>
          </div>
        </Card>
      )}

      {funds && funds.length === 0 && !fundsLoading && (
        <Card>
          <BodyText className="text-secondary text-center py-8">
            No funds match the current filters
          </BodyText>
        </Card>
      )}

      {funds && funds.length > 0 && (
        <Card>
          <TableScroll label="Funds">
            <table className="w-full text-sm font-sans">
              <thead>
                <tr className="border-b border-recessed">
                  <th className="text-left py-3 px-2 text-secondary font-medium">Fund Name</th>
                  <th className="text-left py-3 px-2 text-secondary font-medium">Manager</th>
                  <th className="text-left py-3 px-2 text-secondary font-medium">Strategy</th>
                  <th className="text-right py-3 px-2 text-secondary font-medium">Vintage</th>
                  <th className="text-right py-3 px-2 text-secondary font-medium">Fund Size</th>
                  <th className="text-center py-3 px-2 text-secondary font-medium">Status</th>
                  <th className="text-center py-3 px-2 text-secondary font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {funds.map((fund) => (
                  <FundRow
                    key={fund.id}
                    fund={fund}
                    isExpanded={expandedFundId === fund.id}
                    onToggle={() => toggleExpand(fund.id)}
                    performance={expandedFundId === fund.id ? performance : undefined}
                    lpInterests={expandedFundId === fund.id ? lpInterests : undefined}
                    capitalActivity={expandedFundId === fund.id ? capitalActivity : undefined}
                    detailLoading={expandedFundId === fund.id ? detailLoading : false}
                  />
                ))}
              </tbody>
            </table>
          </TableScroll>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fund Row + Expandable Detail
// ---------------------------------------------------------------------------

// Internal: not an API contract
interface FundRowProps {
  fund: PeFund;
  isExpanded: boolean;
  onToggle: () => void;
  performance?: FundPerformanceMetrics;
  lpInterests?: LpInterest[];
  capitalActivity?: CapitalActivity[];
  detailLoading: boolean;
}

function FundRow({
  fund,
  isExpanded,
  onToggle,
  performance,
  lpInterests,
  capitalActivity,
  detailLoading,
}: FundRowProps) {
  return (
    <>
      <tr
        className={`border-b border-surface cursor-pointer transition-colors ${
          isExpanded ? 'bg-accent/5' : 'hover:bg-elevated'
        }`}
        onClick={onToggle}
      >
        <td className="py-3 px-2 font-medium text-accent">{fund.name}</td>
        <td className="py-3 px-2 text-primary">{fund.managerName}</td>
        <td className="py-3 px-2 text-primary">{capitalizeLabel(fund.strategy)}</td>
        <td className="py-3 px-2 text-right font-mono text-primary">{fund.vintageYear}</td>
        <td className="py-3 px-2 text-right font-mono text-primary">
          {formatCurrency(fund.fundSize)}
        </td>
        <td className="py-3 px-2 text-center">
          <span
            className={`inline-block px-2 py-0.5 rounded text-xs font-semibold capitalize ${
              STATUS_STYLES[fund.fundStatus] || 'bg-recessed text-secondary'
            }`}
          >
            {fund.fundStatus}
          </span>
        </td>
        <td className="py-3 px-2 text-center">
          <Button variant="secondary" onClick={onToggle}>
            {isExpanded ? 'Collapse' : 'Details'}
          </Button>
        </td>
      </tr>

      {isExpanded && (
        <tr>
          <td colSpan={7} className="p-0">
            <div className="p-4 bg-recessed border-b border-surface">
              {detailLoading && (
                <BodyText className="text-secondary text-center py-6">
                  Loading fund details...
                </BodyText>
              )}

              {!detailLoading && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Performance */}
                  <div className="bg-surface rounded p-4 border border-recessed">
                    <Heading level={3} as={3} size="label" className="mb-3 text-sm">Performance</Heading>
                    {performance ? (
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <BodyText className="text-secondary text-sm">TVPI</BodyText>
                          <MetricDisplay
                            value={formatMultiple(performance.tvpiMultiple)}
                            className="text-primary text-sm"
                          />
                        </div>
                        <div className="flex justify-between">
                          <BodyText className="text-secondary text-sm">DPI</BodyText>
                          <MetricDisplay
                            value={formatMultiple(performance.dpiMultiple)}
                            className="text-primary text-sm"
                          />
                        </div>
                        <div className="flex justify-between">
                          <BodyText className="text-secondary text-sm">RVPI</BodyText>
                          <MetricDisplay
                            value={formatMultiple(performance.rvpiMultiple)}
                            className="text-primary text-sm"
                          />
                        </div>
                        <div className="flex justify-between">
                          <BodyText className="text-secondary text-sm">Net IRR</BodyText>
                          <MetricDisplay
                            value={formatPercent(performance.netIrr ?? 0)}
                            className="text-primary text-sm"
                          />
                        </div>
                        <div className="flex justify-between pt-2 border-t border-surface">
                          <BodyText className="text-secondary text-sm">J-Curve Position</BodyText>
                          <BodyText className="text-primary text-sm font-medium">
                            {capitalizeLabel(performance.jCurvePosition)}
                          </BodyText>
                        </div>
                      </div>
                    ) : (
                      <BodyText className="text-tertiary text-sm">No performance data</BodyText>
                    )}
                  </div>

                  {/* LP Interests */}
                  <div className="bg-surface rounded p-4 border border-recessed lg:col-span-2">
                    <Heading level={3} as={3} size="label" className="mb-3 text-sm">LP Interests</Heading>
                    {lpInterests && lpInterests.length > 0 ? (
                      <TableScroll label="LP interests">
                        <table className="w-full text-xs font-sans">
                          <thead>
                            <tr className="border-b border-recessed">
                              <th className="text-left py-1 text-secondary font-medium">LP</th>
                              <th className="text-right py-1 text-secondary font-medium">Commitment</th>
                              <th className="text-right py-1 text-secondary font-medium">Called</th>
                              <th className="text-right py-1 text-secondary font-medium">Distributed</th>
                              <th className="text-right py-1 text-secondary font-medium">NAV</th>
                              <th className="text-center py-1 text-secondary font-medium">Pledged</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lpInterests.map((lp, idx) => (
                              <tr key={idx} className="border-b border-surface">
                                <td className="py-1.5 text-primary">{lp.lpName}</td>
                                <td className="py-1.5 text-right font-mono text-primary">
                                  {formatCurrency(lp.commitmentAmount)}
                                </td>
                                <td className="py-1.5 text-right font-mono text-primary">
                                  {formatCurrency(lp.calledAmount)}
                                </td>
                                <td className="py-1.5 text-right font-mono text-primary">
                                  {formatCurrency(lp.distributedAmount)}
                                </td>
                                <td className="py-1.5 text-right font-mono text-primary">
                                  {formatCurrency(lp.currentNav)}
                                </td>
                                <td className="py-1.5 text-center">
                                  <span
                                    className={`inline-block px-1.5 py-0.5 rounded text-xs ${
                                      lp.isPledged
                                        ? 'bg-warning/10 text-warning'
                                        : 'bg-recessed text-tertiary'
                                    }`}
                                  >
                                    {lp.isPledged ? 'Pledged' : 'Unpledged'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </TableScroll>
                    ) : (
                      <BodyText className="text-tertiary text-sm">No LP interest data</BodyText>
                    )}
                  </div>

                  {/* Capital Activity */}
                  <div className="bg-surface rounded p-4 border border-recessed lg:col-span-3">
                    <Heading level={3} as={3} size="label" className="mb-3 text-sm">Capital Activity</Heading>
                    {capitalActivity && capitalActivity.length > 0 ? (
                      <TableScroll label="Capital activity">
                        <table className="w-full text-xs font-sans">
                          <thead>
                            <tr className="border-b border-recessed">
                              <th className="text-left py-1 text-secondary font-medium">Date</th>
                              <th className="text-left py-1 text-secondary font-medium">Type</th>
                              <th className="text-right py-1 text-secondary font-medium">Amount</th>
                              <th className="text-left py-1 text-secondary font-medium">Purpose</th>
                            </tr>
                          </thead>
                          <tbody>
                            {capitalActivity.map((activity, idx) => (
                              <tr key={idx} className="border-b border-surface">
                                <td className="py-1.5 text-primary font-mono">
                                  {formatDate(activity.activityDate)}
                                </td>
                                <td className="py-1.5 text-primary">
                                  {capitalizeLabel(activity.activityType)}
                                </td>
                                <td className="py-1.5 text-right font-mono text-primary">
                                  {formatCurrency(activity.amount)}
                                </td>
                                <td className="py-1.5 text-tertiary">{activity.purpose}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </TableScroll>
                    ) : (
                      <BodyText className="text-tertiary text-sm">No capital activity data</BodyText>
                    )}
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
